import { gql } from '@apollo/client';
import {
  BRAND_FULL_FRAGMENT,
  BRAND_MINIMAL_FRAGMENT,
  STORE_FULL_FRAGMENT,
  STORE_MINIMAL_FRAGMENT,
  SHOP_FULL_FRAGMENT,
  SHOP_MINIMAL_FRAGMENT,
  PROMOTION_FULL_FRAGMENT,
  PROMOTION_MINIMAL_FRAGMENT,
  PROMOTION_CARD_FRAGMENT,
  PROMOTION_OFFER_FRAGMENT,
  SEO_OVERRIDES_FRAGMENT,
  MEDIA_FRAGMENT,
} from './fragments';

export const LOGIN_USER = gql`
  query LoginUser($userName: String!, $password: String!) {
    loginUser(userName: $userName, password: $password) {
      user {
        id
        email
        avatar
        userName
        twoFactorEnabled
        fullName
      }
      token
    }
  }
`;

// ─── BRANDS ─────────────────────────────────────────────────────

// Rich version: returns paginated object
export const PUBLISHED_BRANDS_RICH = gql`
  query PublishedBrandsRich($options: Options) {
    publishedBrands(options: $options) {
      pagination {
        totalRecords
        currentPage
        limit
        totalPages
      }
      brands {
        ...BrandMinimalFields
      }
    }
  }
  ${BRAND_MINIMAL_FRAGMENT}
`;

// Detail queries take the page's `country` (the route's `/{cc}/{lang}`; none on an un-prefixed
// legacy id URL, where the entity's own country decides): its stores, promotions and `urlPath`
// are that country's (backend `brandById(id, country)`).
// Brand detail
export const BRAND_BY_ID = gql`
  query BrandById($id: ID!, $country: String) {
    brandById(id: $id, country: $country) {
      ...BrandFullFields
    }
  }
  ${BRAND_FULL_FRAGMENT}
`;

// ─── STORES ──────────────────────────────────────────────────────

export const PUBLISHED_STORES_RICH = gql`
  query PublishedStoresRich($options: Options) {
    publishedStores(options: $options) {
      pagination {
        totalRecords
        currentPage
        limit
        totalPages
      }
      stores {
        ...StoreMinimalFields
      }
    }
  }
  ${STORE_MINIMAL_FRAGMENT}
`;

export const STORE_BY_ID = gql`
  query StoreById($id: ID!, $country: String) {
    storeById(id: $id, country: $country) {
      ...StoreFullFields
    }
  }
  ${STORE_FULL_FRAGMENT}
`;

// ─── SHOPS ──────────────────────────────────────────────────────

export const PUBLISHED_SHOPS_RICH = gql`
  query PublishedShopsRich($options: Options) {
    publishedShops(options: $options) {
      pagination {
        totalRecords
        currentPage
        limit
        totalPages
      }
      shops {
        ...ShopMinimalFields
      }
    }
  }
  ${SHOP_MINIMAL_FRAGMENT}
`;

/**
 * Only the published-shop count, for the site-wide navigation: `/shops` is
 * linked from the header, drawer and footer only while it has something to
 * list (web plan W0.3, master D-5). Asked with `limit: 1` and without the
 * `shops` selection, so the response is just the count.
 */
export const PUBLISHED_SHOPS_COUNT = gql`
  query PublishedShopsCount($options: Options) {
    publishedShops(options: $options) {
      pagination {
        totalRecords
      }
    }
  }
`;

// ─── Nearby (server-side geo search) ────────────────────────────────────────
// Backed by the backend 2dsphere index ($geoNear): returns only PUBLISHED
// stores/shops within `radiusKm` of (lat,lng), sorted nearest-first, each with a
// server-computed `distanceKm`. Replaces the old "load the whole catalog +
// client haversine" path. `address` is requested here (the minimal fragment
// omits it) so the list rows can show it.
export const NEARBY_STORES = gql`
  query NearbyStores($lat: Float!, $lng: Float!, $radiusKm: Float, $options: Options) {
    nearbyStores(lat: $lat, lng: $lng, radiusKm: $radiusKm, options: $options) {
      pagination {
        totalRecords
      }
      stores {
        ...StoreMinimalFields
        distanceKm
        address
        # A store rarely carries promotions of its own — they are published on
        # the BRAND and apply at all of its stores. Without this the "Deals"
        # filter matched nothing, however many deals were running nearby.
        # The backend's list of the offers valid here (backend B3.7: the
        # store's own and its brand's store-wide ones, running or upcoming,
        # batched per page), which NearbyPage counts.
        activePromotions {
          id
        }
      }
    }
  }
  ${STORE_MINIMAL_FRAGMENT}
`;

export const NEARBY_SHOPS = gql`
  query NearbyShops($lat: Float!, $lng: Float!, $radiusKm: Float, $options: Options) {
    nearbyShops(lat: $lat, lng: $lng, radiusKm: $radiusKm, options: $options) {
      pagination {
        totalRecords
      }
      shops {
        ...ShopMinimalFields
        distanceKm
        address
      }
    }
  }
  ${SHOP_MINIMAL_FRAGMENT}
`;

export const SHOP_BY_ID = gql`
  query ShopById($id: ID!, $country: String) {
    shopById(id: $id, country: $country) {
      ...ShopFullFields
    }
  }
  ${SHOP_FULL_FRAGMENT}
`;

// ─── PROMOTIONS ─────────────────────────────────────────────────

export const PUBLISHED_PROMOTIONS_RICH = gql`
  query PublishedPromotionsRich($options: Options) {
    publishedPromotions(options: $options) {
      pagination {
        totalRecords
        currentPage
        limit
        totalPages
      }
      promotions {
        ...PromotionCardFields
        viewCounts
        clickCounts
        categories {
          id
          title
        }
        categoriesPaths
        tags {
          id
          title
        }
      }
    }
  }
  ${PROMOTION_CARD_FRAGMENT}
`;

export const PROMOTION_BY_ID = gql`
  query PromotionById($id: ID!, $country: String) {
    promotionById(id: $id, country: $country) {
      ...PromotionFullFields
    }
  }
  ${PROMOTION_FULL_FRAGMENT}
`;

// ─── URL RESOLUTION ─────────────────────────────────────────────

/**
 * What a detail URL whose key is not an ObjectId must do (backend B1.4, web
 * plan W0.16): `status` 200 → load `id`, 301 → go to `canonicalPath`, 404 /
 * 410 → not found. `key` is the route's slug (or old slug), `locale` the
 * route's `pk-en` prefix (omitted on un-prefixed routes). A retryable error
 * (country data unavailable) comes back as an error, never as a 404.
 * Never normalised in the cache (`EntityPathResolution` in @/lib/apollo): two
 * keys of one entity answer different statuses with the same id.
 */
export const RESOLVE_ENTITY_PATH = gql`
  query ResolveEntityPath($type: EntityType!, $key: String!, $locale: String) {
    resolveEntityPath(type: $type, key: $key, locale: $locale) {
      status
      id
      canonicalPath
      robots
      phase
    }
  }
`;

// ─── CATEGORIES ─────────────────────────────────────────────────

// Top-level (user-facing) categories — level 1 in the DB because the seed
// wraps everything under a synthetic "all" root at level 0. `urlPath` and
// `seo.indexable` (backend B3.7b): the home rail and the footer's "Top
// categories" link a hub only while it is indexable (master D-5, web plan W3.1).
export const TOP_CATEGORIES = gql`
  query TopCategories($country: String) {
    topCategories(country: $country) {
      id
      title
      path
      level
      urlPath
      seo {
        indexable
      }
      subCategories {
        id
        title
        path
        level
      }
    }
  }
`;

/**
 * A category hub `/pk/en/category/:l1/:l2?` (backend B3.7b `categoryPage`, web
 * plan W3.1): `path` is `l1` or `l1/l2`; an unknown path, a level-2 slug that
 * is not a child of `l1`, the root and level 3 answer NOT_FOUND. The SPA page
 * and the SEO server send this one document, so the head the server builds is
 * the one the page shows. `seo` is the backend's index decision (D-5
 * threshold; HUB_NOT_LISTED_YET while its PUBLIC_HUB_TYPES leaves categories
 * out); a brand / parent is linked from the hub only while its own
 * `seo.indexable` (D-5: never link a noindex hub from another hub).
 */
export const CATEGORY_PAGE = gql`
  query CategoryPage($path: String!, $locale: String) {
    categoryPage(path: $path, locale: $locale) {
      urlPath
      category {
        id
        title
        path
        level
        urlPath
        seoLabel
        description
        seoOverrides {
          ...SeoOverridesFields
        }
        parentCategory {
          id
          title
          urlPath
          seoLabel
          seo {
            indexable
          }
        }
      }
      livePromotions {
        ...PromotionCardFields
        ...PromotionOfferFields
      }
      upcomingPromotions {
        ...PromotionCardFields
        ...PromotionOfferFields
      }
      brandCount
      brands {
        id
        name
        seoName
        urlPath
        logo {
          ...MediaFields
        }
        seo {
          indexable
        }
      }
      subCategories {
        id
        title
        urlPath
        seoLabel
      }
      seo {
        indexable
        reason
        robots
      }
    }
  }
  ${PROMOTION_CARD_FRAGMENT}
  ${PROMOTION_OFFER_FRAGMENT}
  ${SEO_OVERRIDES_FRAGMENT}
  ${MEDIA_FRAGMENT}
`;

/**
 * A city hub `/{cc}/{lang}/city/:city` (backend B3.7b `cityPage`, web plan
 * W3.2): `slug` is the city's slug in the page's country, or an old one
 * (renamed / merged city). The SPA page and the SEO server send this one
 * document. `seo` is the backend's index decision (D-5: ≥ 20 visible brands;
 * HUB_NOT_LISTED_YET while `PUBLIC_HUB_TYPES` leaves `city` out). A brand or
 * nearby city hub is linked from the hub only while its own `seo.indexable`
 * (D-5: never link a noindex hub from another hub). `brandCityPath` is present
 * only while the brand × city page is indexable (≥ 3 stores there).
 */
export const CITY_HUB_PAGE = gql`
  query CityHubPage($slug: String!, $locale: String) {
    cityPage(slug: $slug, locale: $locale) {
      urlPath
      city {
        name
        slug
        urlPath
      }
      livePromotions {
        ...PromotionCardFields
        ...PromotionOfferFields
      }
      upcomingPromotions {
        ...PromotionCardFields
        ...PromotionOfferFields
      }
      brandCount
      brands {
        brand {
          id
          name
          seoName
          urlPath
          logo {
            ...MediaFields
          }
          seo {
            indexable
          }
        }
        storeCount
        brandCityPath
      }
      categories {
        category {
          id
          title
          urlPath
          seoLabel
          seo {
            indexable
          }
        }
        brandCount
      }
      nearbyCities {
        name
        slug
        urlPath
        seo {
          indexable
        }
      }
      seo {
        indexable
        reason
        robots
      }
    }
  }
  ${PROMOTION_CARD_FRAGMENT}
  ${PROMOTION_OFFER_FRAGMENT}
  ${MEDIA_FRAGMENT}
`;

/**
 * A brand × city hub `/pk/en/brand/:brand/:city` (backend `brandCityPage`,
 * web plan W3.3): `brand` is the brand's slug or id, `city` is the city's slug.
 * The SPA page and the SEO server send this one document. `seo` is the backend's
 * index decision (D-5: ≥ 3 publicly visible stores in the city).
 */
export const BRAND_CITY_PAGE = gql`
  query BrandCityPage($brand: String!, $city: String!, $locale: String) {
    brandCityPage(brand: $brand, city: $city, locale: $locale) {
      urlPath
      brand {
        id
        name
        seoName
        urlPath(locale: $locale)
        logo {
          ...MediaFields
        }
        seo {
          indexable
        }
      }
      city {
        name
        slug
        urlPath(locale: $locale)
      }
      stores {
        id
        name
        displayName
        address
        area
        phoneNumber
        openingHours {
          days
          opens
          closes
        }
        location
        urlPath(locale: $locale)
      }
      storeCount
      livePromotions {
        ...PromotionCardFields
        ...PromotionOfferFields
      }
      upcomingPromotions {
        ...PromotionCardFields
        ...PromotionOfferFields
      }
      seo {
        indexable
        reason
        robots
      }
    }
  }
  ${PROMOTION_CARD_FRAGMENT}
  ${PROMOTION_OFFER_FRAGMENT}
  ${MEDIA_FRAGMENT}
`;

/**
 * Indexable city hubs for the footer's "Deals by city" section and the home
 * page city block (web plan W3.2): cities with publicly visible stores
 * (`withStores: true`), each with their hub path and index policy. The client
 * filters by `seo.indexable`; capped at 30 to cover all ~25 indexable PK cities.
 */
export const NAV_CITIES = gql`
  query NavCities($country: String, $locale: String) {
    cities(country: $country, withStores: true, options: { paginationOptions: { limit: 30 } }) {
      cities {
        name
        slug
        urlPath(locale: $locale)
        seo {
          indexable
        }
      }
    }
  }
`;

// Children of a parent category (pass null/no parent for top-level).
// We include `subCategories { id }` so the UI can tell leaves from branches
// in one round-trip — the resolver always knows the children count anyway.
export const CATEGORIES_BY_PARENT = gql`
  query CategoriesByParent($parentId: ID, $country: String) {
    categoriesByParent(parentId: $parentId, country: $country) {
      id
      title
      path
      level
      parentCategory {
        id
        title
        path
      }
      subCategories {
        id
      }
    }
  }
`;

// The single-category lookups (categoryById / categoryByPath /
// categoryAncestors) and their hooks lived here but had no call sites, and they
// are not part of the public end-user schema — the picker navigates with
// topCategories + categoriesByParent. Removed with the endpoint split
// (PLAN.md T5.5).

// ─── SALE EVENT HUBS (web plan W3.4) ────────────────────────────────────────

/**
 * A sale event hub `/{cc}/{lang}/sale/{slug}` (backend B3.7a `salePage`; web plan W3.4):
 * `slug` = event slug; `locale` = launched locale code ("pk-en"). NOT_FOUND
 * "Sale Event Not Found" unless the event is published in the page's country.
 * The SPA page and the SSR server send this one document.
 * `livePromotions` (ACTIVE, ending soonest) + `upcomingPromotions` (starting
 * soonest) replace the old paginated publishedPromotions-with-tag approach.
 */
export const SALE_PAGE = gql`
  query SalePage($slug: String!, $locale: String) {
    salePage(slug: $slug, locale: $locale) {
      event {
        id
        slug
        name
        aliases
        description
        nextWindow {
          year
          start
          end
          expected
        }
      }
      nextWindow {
        year
        start
        end
        expected
      }
      live
      previousWindow {
        year
        start
        end
        expected
      }
      livePromotions {
        ...PromotionCardFields
        ...PromotionOfferFields
      }
      upcomingPromotions {
        ...PromotionCardFields
        ...PromotionOfferFields
      }
      previousParticipants {
        year
        brandName
        brand {
          id
          urlPath
        }
        start
        end
        maxDiscount
      }
      seo {
        indexable
        reason
        robots
        alternates {
          hreflang
          url
        }
      }
    }
  }
  ${PROMOTION_CARD_FRAGMENT}
  ${PROMOTION_OFFER_FRAGMENT}
`;

/**
 * Which launched locales have published sale events (backend MC10
 * `locales { hasSaleEvents }`, web plan MW8): the one switch of the sale hub,
 * the home Sale calendar block, the footer link, the country selector and the
 * SEO server. Asked once per session (the Apollo cache) and by the SEO server
 * through its cache; a failed answer leaves the safe fallback of
 * @/seo/saleCountries (Pakistan keeps its sale pages).
 */
export const SALE_LOCALES = gql`
  query SaleLocales {
    locales {
      code
      country
      hasSaleEvents
    }
  }
`;

/**
 * Published sale events for the calendar `/{cc}/{lang}/sales` (web plan W3.4).
 * The backend returns only published events in the request's country.
 * Used by `SalesCalendarPage` and the home-page sale rail.
 */
export const SALE_EVENTS_LIST = gql`
  query SaleEventsList($options: Options) {
    saleEvents(options: $options) {
      id
      slug
      name
      aliases
      description
      nextWindow {
        year
        start
        end
        expected
      }
      windows {
        year
        start
        end
        expected
      }
      urlPath
    }
  }
`;

// ─── FAVORITES ──────────────────────────────────────────────────

// ─── BRAND HUB PAGE (web plan W3.6) ────────────────────────────────────────

/**
 * The brand hub's extra data (web plan W3.6): sale history, similar brands
 * and SEO policy. The entity query (`BRAND_BY_ID` via `useBrandDetail`) already
 * returns livePromotions/upcomingPromotions/stores/cityCounts; this query adds
 * the fields that only `brandPage` carries.
 *
 * `urlPath(locale: $locale)` on `SaleHistoryEntry` is null once the
 * promotion's page resolves 410 (> 90 days after end) — the client must
 * render those rows as plain text.
 */
export const BRAND_PAGE_HUB = gql`
  query BrandPageHub($slug: String!, $locale: String) {
    brandPage(slug: $slug, locale: $locale) {
      brand {
        id
        name
        seoName
        urlPath(locale: $locale)
        seo {
          indexable
        }
      }
      saleHistory {
        id
        headline
        startDate
        endDate
        discountType
        discountMin
        discountMax
        urlPath(locale: $locale)
      }
      similarBrands {
        id
        name
        urlPath(locale: $locale)
        logo {
          ...MediaFields
        }
        seo {
          indexable
        }
      }
      storeCount
      seo {
        indexable
        reason
        robots
      }
    }
  }
  ${MEDIA_FRAGMENT}
`;

// IS_BRAND_FAVORITE / IS_STORE_FAVORITE / IS_SHOP_FAVORITE lived here but were
// never imported anywhere, and `isBrandFavorite` & co. have never existed on the
// backend schema — they would have errored on first use. Favourite state comes
// from the `isFavorite` field on Brand/Store/Shop instead. Removed as part of
// the API audience split (PLAN.md T5.5).
export const GET_FAVORITES = gql`
  query GetFavorites($country: String) {
    favorites(country: $country) {
      id
      brands {
        ...BrandMinimalFields
        promotions {
          id
        }
      }
      stores {
        ...StoreMinimalFields
        promotions {
          id
        }
      }
      shops {
        ...ShopMinimalFields
        promotions {
          id
        }
      }
    }
  }
  ${BRAND_MINIMAL_FRAGMENT}
  ${STORE_MINIMAL_FRAGMENT}
  ${SHOP_MINIMAL_FRAGMENT}
`;
